'use client'
import "../shared/reset-input.css"
import {ChangeEvent, CSSProperties, FocusEvent, HTMLInputTypeAttribute, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import textFieldStyles from "./text-field.module.css";
import {defineFormField, FormFieldProps} from "@web-libs/core/forms/ui/fields/shared/form-field.types";
import {FieldLabel} from "@web-libs/core/forms/ui/fields/shared/field-label";
import {FieldError} from "@web-libs/core/forms/ui/fields/shared/field-error";
import {Color, getCssVar} from "@web-libs/features/colors/application/colors";

export type TextFieldProps = FormFieldProps<string> & {
    width?: number
    fitParent?: boolean
    suffix?: ReactNode
    obscure?: boolean
    color?: TextFieldColor,
    readOnly?: boolean,
    style?: CSSProperties,
    type?: HTMLInputTypeAttribute,
}

export type TextFieldTheme = {
    baseColor: Color
    focusedColor: Color
}

type ThemeOptions = "lightOnPrimary" | "light" | "dark" | "onPrimary"

export const textFieldColorMap: Partial<{ [key in ThemeOptions]: TextFieldTheme }> = {
    lightOnPrimary: {
        baseColor: "light",
        focusedColor: "light"
    },
    light: {
        baseColor: "light",
        focusedColor: "primary"
    },
    dark: {
        baseColor: "dark",
        focusedColor: "primary"
    },
    onPrimary: {
        baseColor: "onPrimary",
        focusedColor: "onPrimary"
    }
}

export type TextFieldColor = keyof typeof textFieldColorMap

export function getTextFieldColor(color: TextFieldColor): Color {
    return textFieldColorMap[color]?.baseColor ?? textFieldColorMap.dark?.baseColor!
}

export const TextField = defineFormField<TextFieldProps, HTMLInputElement, string>(function (props, ref) {
    const [focused, setFocused] = useState(false)
    const [inputValue, setInputValue] = useState(props.value)
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (inputRef.current && inputRef.current?.value) {
            setInputValue(inputRef.current.value);
        }
    }, [])

    useEffect(() => {
        if(inputRef.current && props.value){
            setInputValue(props.value);
            inputRef.current.value = props.value;
        }
    }, [props.value])

    const floatLabel = useMemo(() => {
        return !!(focused || inputValue)
    }, [focused, props.value, inputValue])

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        setFocused(false)
       props.onBlur?.(event)
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value)
        props.onChange?.(event)
    }

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
        setFocused(true)
    }

    const focusedStyle = focused ? textFieldStyles.focused : ''
    const errorStyle = props.error ? textFieldStyles.error : ''
    const containerClass = `${textFieldStyles.fieldContainer} ${focusedStyle} ${errorStyle}`

    const _wrapperStyle: CSSProperties = {...props.style}
    if (props.width) {
        _wrapperStyle.width = props.width
    } else if (props.fitParent) {
        _wrapperStyle.flexGrow = props.style?.flexGrow ??  1
        _wrapperStyle.width = "100%"
    } else {
        _wrapperStyle.width = 300
    }

    const _inputType = props.obscure ? "password" : (props.type ?? "text")

    const _color = props.color ?? "dark"
    const theme = textFieldColorMap[_color] ?? textFieldColorMap.dark!
    const borderColor = props.error ? getCssVar("warn") : focused ? getCssVar(theme.focusedColor) : getCssVar(theme.baseColor)
    const containerStyle: CSSProperties = {
        borderColor: props.readOnly ? getCssVar("unobtrusive") : borderColor
    }
    const inputStyle = {
        color: getCssVar(props.readOnly ? "unobtrusive" :theme.baseColor)
    }
    return (
        <div style={_wrapperStyle}>
            <div style={containerStyle} className={containerClass}>
                <FieldLabel baseColor={props.readOnly ? "unobtrusive" :  theme.baseColor} focusedColor={theme.focusedColor} label={props.label}
                            error={!!props.error} focused={focused} float={floatLabel}/>
                <input  name={props.name} onChange={handleChange}  ref={(element) => {
                    if (typeof ref === "function") {
                        ref(element);
                    } else if (ref) {
                        ref.current = element;
                    }
                    inputRef.current = element;
                }} onFocus={handleFocus}
                       onBlur={handleBlur}
                       style={{fontSize: 20, width: "100%", ...inputStyle}} type={_inputType} disabled={props.readOnly}  />
                {props.suffix &&
                    <div style={{paddingLeft: 8, paddingRight: 4, display: "flex", alignItems: "center"}}>
                        {props.suffix}</div>}
            </div>
            <FieldError error={props.error}/>
        </div>
    )
})
